const STREAM_ENDPOINT_PATH = '/api/public/stream';

export class StreamLookupError extends Error {
  constructor(code, message, options) {
    super(message, options);
    this.code = code;
  }
}

// @webble/wc-player's createWebblePlayer always resolves a stream by its
// internal Webble-Cast uuid - it has no notion of "external" ids. When the
// URL's stream_id_type is external, we resolve the internal uuid ourselves
// (the public stream endpoint accepts stream_id_type as a query param) and
// then hand that internal id to createWebblePlayer, so it still gets full
// Webble-Cast mode (MQTT live updates, pancarte, player_settings, etc.).
export async function resolveInternalStreamId(instanceUrl, streamId, streamIdType, authToken) {
  if (streamIdType === 'internal') {
    return streamId;
  }

  const base = instanceUrl.replace(/\/+$/, '');
  const url = `${base}${STREAM_ENDPOINT_PATH}/${encodeURIComponent(streamId)}?stream_id_type=${encodeURIComponent(streamIdType)}`;

  let response;
  try {
    response = await fetch(url, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    });
  } catch (cause) {
    throw new StreamLookupError('network_error', 'Could not reach the streaming instance.', { cause });
  }

  if (response.status === 401) {
    throw new StreamLookupError('unauthorized', 'Not authorized to view this stream.');
  }
  if (response.status === 403) {
    throw new StreamLookupError('forbidden', 'Not allowed to view this stream.');
  }
  if (response.status === 404) {
    throw new StreamLookupError('not_found', 'This stream could not be found.');
  }
  if (!response.ok) {
    throw new StreamLookupError('malformed_response', 'Unexpected response from the streaming instance.');
  }

  let body;
  try {
    body = await response.json();
  } catch (cause) {
    throw new StreamLookupError('malformed_response', 'Unexpected response from the streaming instance.', { cause });
  }

  const internalId = body?.data?.uuid;
  if (typeof internalId !== 'string' || !internalId) {
    throw new StreamLookupError('malformed_response', 'Stream lookup response did not include a stream id.');
  }

  return internalId;
}
